import React, { createContext, ReactElement, ReactNode, useState } from 'react'
import { AddressFormFields } from 'src/component-form/AddressForm/AddressForm'
import { BinRangeResponse } from 'src/models/binRange.models'

export type AstradaCardData = {
  number: string
  expiration: string
  cvc?: string
}

export type AstradaBinData = {
  id: string
  issuerId?: string
  rangeMin: number
  rangeMax: number
  supported: boolean
  issuerLogoImg?: string
  issuerRequiresVerification?: boolean
  astradaEnabled?: boolean
  eligibility:
    | 'PHYSICAL_ONLY'
    | 'VIRTUAL_ONLY'
    | 'PHYSICAL_AND_VIRTUAL'
    | 'INELIGIBLE'
}

type AstradaContextActions = {
  /** Updates BinData with calculated astrada eligibility */
  updateBinData: (binData: BinRangeResponse) => void
  /** Performs a shallow update of the full astrada context state */
  updateState: (data: AstradaContextState) => void

  updateCardData: (data: AstradaCardData) => void

  updateAddressData: (data: AstradaContextState['addressData']) => void
  /**
   * resets Astrada context to the original state. Use when you are finished
   * enrolling a card with astrada and 3DS verification to allow for another
   * card to be registered
   */
  reset: () => void
}

type AstradaContextState = {
  binData?: AstradaBinData
  cardData?: AstradaCardData
  addressData?: AddressFormFields
  issuerData?: { [key: string]: string }
  onboarding?: boolean
}

export type AstradaContextValue = AstradaContextActions & AstradaContextState

const stubAstradaContextActions: AstradaContextActions = {
  updateBinData: () => {
    throw Error('AstradaContext Not Available')
  },
  updateState: () => {
    throw Error('AstradaContext Not Available')
  },
  updateCardData: () => {
    throw Error('AstradaContext Not Available')
  },
  updateAddressData: () => {
    throw Error('AstradaContext Not Available')
  },
  reset: () => {
    throw Error('AstradaContext Not Available')
  },
}

const defaultAstradaContextState: AstradaContextState = {}

export const AstradaContext = createContext<AstradaContextValue>({
  ...defaultAstradaContextState,
  ...stubAstradaContextActions,
})

type AstradaContextProviderProps = {
  children: ReactNode
}

export function calculateAstradaEligibility(params: {
  partnerEnabled: boolean
  astradaEnabled: boolean
}): AstradaBinData['eligibility'] {
  const { partnerEnabled, astradaEnabled } = params
  if (partnerEnabled && astradaEnabled) {
    return 'PHYSICAL_AND_VIRTUAL'
  } else if (partnerEnabled && !astradaEnabled) {
    return 'VIRTUAL_ONLY'
  } else if (!partnerEnabled && astradaEnabled) {
    return 'PHYSICAL_ONLY'
  } else {
    // !partnerEnabled && !astradaEnabled
    return 'INELIGIBLE'
  }
}

export default function AstradaContextProvider(
  props: AstradaContextProviderProps,
): ReactElement {
  const { children } = props

  const [astradaContextState, setAstradaContextState] =
    useState<AstradaContextState>(defaultAstradaContextState)

  const updateBinData = (binData: BinRangeResponse) => {
    const { partnerEnabled, astradaEnabled, ...rest } = binData
    const eligibility = calculateAstradaEligibility({
      partnerEnabled,
      astradaEnabled,
    })

    setAstradaContextState({
      ...astradaContextState,
      binData: {
        ...rest,
        astradaEnabled,
        eligibility,
      },
      cardData: astradaContextState.cardData
        ? { ...astradaContextState.cardData }
        : undefined,
      addressData: astradaContextState.addressData
        ? { ...astradaContextState.addressData }
        : undefined,
    })
  }

  const updateCardData = (data: AstradaCardData) => {
    setAstradaContextState({
      ...astradaContextState,
      cardData: { ...data },
      binData: astradaContextState.binData
        ? { ...astradaContextState.binData }
        : undefined,
      addressData: astradaContextState.addressData
        ? { ...astradaContextState.addressData }
        : undefined,
    })
  }

  const updateAddressData = (data: AstradaContextState['addressData']) => {
    setAstradaContextState({
      ...astradaContextState,
      binData: astradaContextState.binData
        ? { ...astradaContextState.binData }
        : undefined,
      addressData: { ...(data as AddressFormFields) },
      cardData: astradaContextState.cardData
        ? { ...astradaContextState.cardData }
        : undefined,
    })
  }

  const updateState = (data: AstradaContextState) => {
    setAstradaContextState({
      ...astradaContextState,
      ...data,
    })
  }

  const reset = () => {
    setAstradaContextState({ ...defaultAstradaContextState })
  }

  return (
    <AstradaContext.Provider
      value={{
        ...astradaContextState,
        updateCardData,
        updateAddressData,
        updateBinData,
        updateState,
        reset,
      }}
    >
      {children}
    </AstradaContext.Provider>
  )
}
